"use client";

import type { CareSeekerContractData } from "@homeinstead/forms/types";
import type { ChangeEvent } from "react";
import type { LeadFlowChoiceArrangement } from "../../types";

import { CARE_TYPE_VALUES } from "@homeinstead/forms/lead-form";
import { cn } from "@homeinstead/ui";
import { Checkbox } from "@homeinstead/ui/components/Checkbox";
import { useId, useMemo } from "react";
import { CHOICE_ARRANGEMENT_CLASSES } from "../../constants/choice-arrangement-classes";
import { useAnswer } from "../../hooks/use-answers";
import { useDefaultAnswer } from "../../hooks/use-default-answer";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";
import { useLeadFlowStep } from "../LeadFlowStepContext";
import { ChoiceOption } from "./ChoiceOption";
import { FieldError } from "./FieldError";
import { QuestionLegend } from "./QuestionLegend";

type CareTypeAnswer = CareSeekerContractData["careType"];

type CareTypeValue = (typeof CARE_TYPE_VALUES)[number];

type CareTypeOption = {
  _key: string;
  description?: string | null;
  iconUrl?: string | null;
  label?: string | null;
  value?: string | null;
};

type CareTypeQuestionProps = {
  item: {
    arrangement?: LeadFlowChoiceArrangement | null;
    defaultAnswers?: string[] | null;
    helperText?: string | null;
    label?: string | null;
    options?: CareTypeOption[] | null;
  };
};

function isCareTypeValue(value: string): value is CareTypeValue {
  return (CARE_TYPE_VALUES as readonly string[]).includes(value);
}

/** The contract stores every care type as a flag, so unpicked ones are false. */
function createCareTypeAnswer(selectedValues: string[]): CareTypeAnswer {
  return Object.fromEntries(
    CARE_TYPE_VALUES.map((value) => [value, selectedValues.includes(value)]),
  ) as CareTypeAnswer;
}

function CareTypeQuestion({ item }: CareTypeQuestionProps) {
  const [answer, setAnswer] = useAnswer("careType");
  const { errors, isInert = false } = useLeadFlowStep();
  const { questionStyle } = useLeadFlowRuntime();
  const errorId = useId();
  const arrangement = item.arrangement ?? "list";

  const defaultAnswer = useMemo(
    () =>
      item.defaultAnswers?.length
        ? createCareTypeAnswer(item.defaultAnswers.filter(isCareTypeValue))
        : undefined,
    [item.defaultAnswers],
  );
  useDefaultAnswer("careType", defaultAnswer);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;

    if (isInert || !isCareTypeValue(value)) {
      return;
    }

    const selectedValues = CARE_TYPE_VALUES.filter((candidate) =>
      candidate === value ? !answer?.[candidate] : answer?.[candidate],
    );
    setAnswer(createCareTypeAnswer(selectedValues));
  }

  const options = (item.options ?? []).flatMap((option) =>
    option.value && option.label && isCareTypeValue(option.value)
      ? [{ ...option, label: option.label, value: option.value }]
      : [],
  );
  const fieldErrors = errors.careType;

  return (
    <fieldset
      aria-describedby={fieldErrors?.length ? errorId : undefined}
      className="flex flex-col"
    >
      <QuestionLegend helperText={item.helperText} label={item.label} />
      {questionStyle.multiSelectFormat === "checkbox" ? (
        <div className="flex flex-col gap-3">
          {options.map((option) => (
            <label
              className="flex cursor-pointer items-center gap-3"
              key={option._key}
            >
              <Checkbox
                checked={Boolean(answer?.[option.value])}
                name={`careType.${option.value}`}
                onChange={handleChange}
                value={option.value}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      ) : (
        <div
          className={cn(
            CHOICE_ARRANGEMENT_CLASSES[arrangement],
            // Checkbox cards sit closer together than pills in every arrangement.
            "gap-3",
          )}
        >
          {options.map((option) => (
            <ChoiceOption
              arrangement={arrangement}
              description={option.description}
              iconUrl={option.iconUrl}
              isSelected={Boolean(answer?.[option.value])}
              key={option._key}
              label={option.label}
              name={`careType.${option.value}`}
              onSelect={handleChange}
              type="checkbox"
              value={option.value}
            />
          ))}
        </div>
      )}
      <FieldError id={errorId} messages={fieldErrors} />
    </fieldset>
  );
}

export { CareTypeQuestion };
